import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function CourseHighlights() {
  return (
    <section className="py-20 bg-card">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <span className="text-accent font-semibold text-sm tracking-widest uppercase">The Course</span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 leading-tight">
              18 Holes of Championship<br />
              <span className="text-primary">Golf Since 1886</span>
            </h2>
          </div>
          <p className="text-muted-foreground text-base max-w-sm md:text-right leading-relaxed">
            A Par 72 layout that rewards every level of golfer — from weekend warriors to scratch players.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

          {/* Large feature card — spans 2 cols on lg */}
          <div className="lg:col-span-2 relative rounded-2xl overflow-hidden min-h-[280px] bg-primary group">
            <AppImage
              src="https://images.unsplash.com/photo-1656165732420-10cfda2fe907"
              alt="Aerial view of Maritzburg Golf Club's 18-hole championship course with lush fairways"
              fill
              className="object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-500" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
            <div className="relative p-8 flex flex-col justify-end h-full min-h-[280px]">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-accent/20 border border-accent/30 flex items-center justify-center">
                  <Icon name="TrophyIcon" size={16} variant="outline" className="text-accent" />
                </div>
                <span className="text-accent text-xs font-semibold uppercase tracking-widest">SA&apos;s Oldest Playing Club</span>
              </div>
              <h3 className="text-white font-bold text-2xl mb-2">Par 72 · 18 Holes</h3>
              <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                Generous fairways and receptive greens suit all handicap levels. A meandering stream winds through the course, adding both beauty and strategic challenge to your round.
              </p>
            </div>
          </div>

          {/* Modern Clubhouse */}
          <div className="bg-background border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-5">
              <Icon name="BuildingStorefrontIcon" size={24} variant="outline" className="text-white" />
            </div>
            <h3 className="font-bold text-foreground text-base mb-2">Modern Clubhouse</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Relax on our scenic veranda overlooking the 18th green. Full bar, refreshments, and a welcoming atmosphere after your round.
            </p>
          </div>

          {/* Pro Shop */}
          <div className="bg-background border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-5">
              <Icon name="ShoppingBagIcon" size={24} variant="outline" className="text-white" />
            </div>
            <h3 className="font-bold text-foreground text-base mb-2">The Pro Shop</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Expert advice, quality gear, and professional lessons from our experienced team. Club hire and caddie services available.
            </p>
          </div>

          {/* Padel & Squash — wide card */}
          <div className="md:col-span-2 bg-muted border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center shrink-0">
                <Icon name="UsersIcon" size={24} variant="outline" className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-foreground text-base mb-1">Padel &amp; Squash Courts</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  More than just golf — enjoy a match on our Padel or Squash Courts. Perfect for the whole family and corporate team-building days.
                </p>
              </div>
              <div className="flex gap-3 shrink-0">
                <div className="text-center px-4 py-3 bg-card border border-border rounded-xl">
                  <div className="text-primary font-bold text-lg leading-none">2</div>
                  <div className="text-muted-foreground text-xs mt-1">Padel Courts</div>
                </div>
                <div className="text-center px-4 py-3 bg-card border border-border rounded-xl">
                  <div className="text-primary font-bold text-lg leading-none">2</div>
                  <div className="text-muted-foreground text-xs mt-1">Squash Courts</div>
                </div>
              </div>
            </div>
          </div>

          {/* Water Features */}
          <div className="bg-background border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-md transition-all duration-200">
            <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-5">
              <Icon name="SparklesIcon" size={24} variant="outline" className="text-white" />
            </div>
            <h3 className="font-bold text-foreground text-base mb-2">Scenic Water Features</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              A meandering stream winds through the course, adding both natural beauty and strategic challenge to your round.
            </p>
          </div>
        </div>

        {/* Course stats strip */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
          { value: '18', label: 'Championship Holes', icon: 'FlagIcon' },
          { value: 'Par 72', label: 'Course Rating', icon: 'TrophyIcon' },
          { value: '1886', label: 'Year Established', icon: 'StarIcon' },
          { value: '400+', label: 'Active Members', icon: 'UserGroupIcon' }].
          map((stat) =>
          <div key={stat.label} className="bg-background border border-border rounded-xl px-5 py-4 flex items-center gap-3">
              <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} variant="outline" className="text-accent shrink-0" />
              <div>
                <div className="text-foreground font-bold text-base leading-none">{stat.value}</div>
                <div className="text-muted-foreground text-xs mt-0.5">{stat.label}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}